import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Users, CreditCard, Star, Clock, FileText, Globe, Heart } from 'lucide-react';

const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';
const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';

const IMAGES = {
  hero: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/36p207b8_abba-masaje-a-domicilio-horizontal.png',
  inHome: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/36p207b8_abba-masaje-a-domicilio-horizontal.png',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const benefitIcons = [
  Star, Globe, Clock, CreditCard, FileText, Users, Users, Heart
];

export const Hosts = () => {
  const { t, language } = useLanguage();

  // Spanish content for How It Works section
  const howItWorksContent = language === 'es' ? {
    title: 'Cómo funciona',
    steps: [
      {
        step: '1',
        title: 'Regístrate como socio',
        description: 'Contáctanos para crear tu cuenta de anfitrión y recibir tu enlace de recomendación.',
      },
      {
        step: '2',
        title: 'Compártelo con tus huéspedes',
        description: 'Incluye nuestros servicios en tu guía de bienvenida o en la información de tu propiedad.',
      },
      {
        step: '3',
        title: 'Disfruta los beneficios',
        description: 'Tus huéspedes reciben bienestar profesional y nosotros hacemos que recomendarlo también sea gratificante para ti.',
      },
    ],
    cta: 'Conviértete en socio',
  } : {
    title: 'How it works',
    steps: [
      {
        step: '1',
        title: 'Register as a partner',
        description: 'Contact us to set up your host account and receive your referral link.',
      },
      {
        step: '2',
        title: 'Share with your guests',
        description: 'Include our services in your welcome guide or property information.',
      },
      {
        step: '3',
        title: 'Enjoy the benefits',
        description: 'Your guests get premium wellness, and we make it rewarding for you.',
      },
    ],
    cta: 'Become a partner',
  };

  // CTA section content
  const ctaContent = language === 'es' ? {
    title: '¿Listo para ser socio?',
    description: 'Únete a nuestra red de anfitriones y ofrece a tus huéspedes servicios de bienestar premium.',
  } : {
    title: 'Ready to partner?',
    description: 'Join our network of hosts and offer your guests premium wellness services.',
  };

  return (
    <div data-testid="hosts-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 min-h-[80vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt={language === 'es' ? 'Masaje profesional a domicilio en villa' : 'Professional in-home massage in villa'}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {t('hosts.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {t('hosts.title')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/80 mb-8 font-['Manrope']">
              {t('hosts.description')}
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {t('hosts.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 px-6" data-testid="hosts-benefits">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] tracking-tight">
              {t('hosts.benefits.title')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {t('hosts.benefits.items').map((benefit, index) => {
              const Icon = benefitIcons[index] || Check;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-full bg-[#F8F6F3] flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#A91B60]" />
                  </div>
                  <p className="text-[#1C1C1C] font-['Manrope']">{benefit}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={IMAGES.inHome}
                alt="In-Home Massage Service"
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-8 tracking-tight">
                {howItWorksContent.title.split(' ')[0]} <em className="italic text-[#A91B60]">{howItWorksContent.title.split(' ').slice(1).join(' ')}</em>
              </motion.h2>
              
              <motion.div variants={fadeInUp} className="space-y-6">
                {howItWorksContent.steps.map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#A91B60] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      {step.step}
                    </div>
                    <div>
                      <h4 className="font-['Manrope'] font-semibold text-[#1C1C1C] mb-1">{step.title}</h4>
                      <p className="text-[#5A5A5A] text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
              
              <motion.div variants={fadeInUp} className="mt-8">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {howItWorksContent.cta}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#A91B60]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
              {ctaContent.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10 font-['Manrope']">
              {ctaContent.description}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.contact')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Hosts;
